import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Panel, LoadingState, ErrorState } from "@/components/common";
import { DemoBadge } from "@/components/DemoBadge";
import { ShieldAlert, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export default function RiskManagement() {
  const qc = useQueryClient();
  const [edits, setEdits] = useState({});

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["risk"],
    queryFn: async () => (await api.get("/risk/policies")).data,
  });

  const update = useMutation({
    mutationFn: async ({ id, value, confirm_disable_mandatory }) => (await api.put(`/risk/policies/${id}`, { value, confirm_disable_mandatory: !!confirm_disable_mandatory })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["risk"] }); toast.success("Policy updated (DEMO)"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState label="Failed to load risk policies" onRetry={() => refetch()} />;

  const items = data?.items ?? [];
  const save = (p) => {
    const value = edits[p.id] ?? p.value;
    update.mutate({ id: p.id, value: String(value) });
  };

  return (
    <div>
      <PageHeader title="Risk Management" subtitle="Configurable risk policies · example budget 1% per trade" icon={ShieldAlert} testId="risk-header" />

      <div className="grid md:grid-cols-2 gap-3 mb-4">
        <div className="flex items-center gap-2 rounded-lg border border-amber-500/25 bg-amber-500/10 px-4 py-3">
          <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
          <span className="text-xs text-amber-300">A stop-loss trigger is never a guaranteed execution price. Policies are <b>ENFORCED on every paper (simulated) order</b>; live trading is disabled, so no real positions exist.</span>
        </div>
        <div className="flex items-center gap-2 rounded-lg border border-[var(--border-subtle)] bg-[var(--card)] px-4 py-3">
          <span className="text-[11px] text-slate-400">Enforcement status</span>
          <span data-testid="enforcement-status" className="ml-auto text-[10px] font-mono px-2 py-0.5 rounded border uppercase text-emerald-400 border-emerald-600/40">Enforced · Paper</span>
        </div>
      </div>

      <Panel title="Risk Policies" testId="risk-policies-panel" actions={<DemoBadge />}>
        <div className="divide-y divide-[var(--border-subtle)]">
          {items.map((p) => {
            const isBool = p.unit === "bool";
            const cur = edits[p.id] ?? p.value;
            return (
              <div key={p.id} data-testid={`risk-policy-${p.key}`} className="flex items-center justify-between gap-4 px-4 py-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-200 flex items-center gap-2">
                    {p.name}
                    {p.mandatory && <span className="text-[9px] font-mono px-1.5 py-0.5 rounded border uppercase text-blue-400 border-blue-500/30">Mandatory</span>}
                  </div>
                  <div className="text-[11px] text-slate-500">{p.description}{p.bounds ? ` · range ${p.bounds.min}–${p.bounds.max} ${p.unit}` : ""}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {isBool ? (
                    <Switch
                      data-testid={`risk-switch-${p.key}`}
                      checked={cur === "1"}
                      onCheckedChange={(c) => {
                        const v = c ? "1" : "0";
                        if (!c && p.mandatory && !window.confirm(`Disable the mandatory control "${p.name}"? This protective control should normally stay enabled.`)) return;
                        setEdits({ ...edits, [p.id]: v });
                        update.mutate({ id: p.id, value: v, confirm_disable_mandatory: !c && p.mandatory });
                      }}
                    />
                  ) : (
                    <>
                      <Input
                        data-testid={`risk-input-${p.key}`}
                        value={cur}
                        onChange={(e) => setEdits({ ...edits, [p.id]: e.target.value })}
                        className="h-8 w-24 text-xs text-right font-mono-num bg-[var(--bg-canvas)] border-[var(--border-subtle)]"
                      />
                      <span className="text-[11px] text-slate-500 w-16">{p.unit}</span>
                      <Button size="sm" onClick={() => save(p)} className="h-8 text-xs bg-blue-600 hover:bg-blue-500 text-white">Save</Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Panel>

      <Panel title="Strategy-Specific Trade Frequency" testId="strategy-frequency-panel" className="mt-4" actions={<DemoBadge />}>
        <div className="divide-y divide-[var(--border-subtle)]">
          {(data?.strategy_trade_frequency || []).map((f) => (
            <div key={f.id} className="flex items-center justify-between px-4 py-3">
              <div>
                <div className="text-sm font-medium text-slate-200">{f.label}</div>
                <div className="text-[11px] text-slate-500">{f.applies ? "Monthly qualifying-trade limit applies" : "Frequency limit does not apply to this strategy"}</div>
              </div>
              <span className={cn("text-xs font-mono px-2 py-0.5 rounded border", f.applies ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" : "text-slate-500 border-slate-600/40")}>
                {f.max_trades_per_month != null ? `${f.max_trades_per_month} / month` : "N/A"}
              </span>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
